import { InputOnChangeData, mergeClasses, Popover, PopoverSurface, Title2, tokens } from "@fluentui/react-components";
import _ from "lodash";
import { ChangeEvent, memo, useCallback, useMemo, useState } from "react";
import SellOutlineIcon from "~icons/material-symbols/sell-outline";
import { GroupedMetadataSelector } from "../../../components/GroupedMetadataSelector";
import { useAppData } from "../../../hooks/useAppData";
import { useSuggestionFilterFormStyles } from "../styles/SuggestionFilterForm.styles";
import { ActionButtons } from "./ActionButtons";
import { FilterTextFields } from "./FilterTextFields";
import { GenericPopoverPanel } from "./GenericPopoverPanel";

export type SuggestionFilterFormProps = {
  appId: string;
};

export const SuggestionFilterForm = memo(
  function SuggestionFilterForm({ appId }: SuggestionFilterFormProps) {
    const styles = useSuggestionFilterFormStyles();

    const [filters, setFilters] = useAppData<Record<string, any>>(appId, "filters");
    const [isFilterOpened, setIsFilterOpened] = useAppData<boolean>(appId, "isFilterOpened");
    const [, setPage] = useAppData<number>(appId, "page");

    const [keywordText, setKeywordText] = useState<string>((filters?.keywords as string) ?? "");
    const [title, setTitle] = useState<string>((filters?.title as string) ?? "");

    const [selectedMetadataOptions, setSelectedMetadataOptions] = useState<Record<string, string[]>>(
      _.omit(filters, "keywords", "title")
    );

    const filterCount = useMemo(
      () => _.sumBy(_.values(filters), (v) => (v && !(_.isNil(v) || _.isEmpty(v)) ? 1 : 0)),
      [filters]
    );

    const onUpdateSuggestion = useCallback(() => {
      setPage(0);
      setFilters({
        keywords: keywordText,
        title,
        ...selectedMetadataOptions,
      });
      setIsFilterOpened(false);
    }, [keywordText, title, selectedMetadataOptions, setFilters, setIsFilterOpened]);

    const onKeywordTextChange = useCallback((_ev: ChangeEvent, data: InputOnChangeData) => {
      setKeywordText(data.value as string);
    }, []);

    const onTitleTextChange = useCallback((_ev: ChangeEvent, data: InputOnChangeData) => {
      setTitle(data.value as string);
    }, []);

    const clearFilters = useCallback(() => {
      setKeywordText("");
      setTitle("");
      setSelectedMetadataOptions({});
    }, []);

    return (
      <Popover
        open={isFilterOpened}
        onOpenChange={(_ev, data) => setIsFilterOpened(data.open as boolean)}
        inline
        size="large"
        withArrow
        positioning={"below-end"}
      >
        <GenericPopoverPanel
          content="Filter search results"
          isOpen={isFilterOpened}
          count={filterCount}
          icon={<SellOutlineIcon color={isFilterOpened ? tokens.colorBrandBackground : ""} />}
        />
        <PopoverSurface id={"suggestion-filter-form"} className={styles.filterContainerSurface}>
          <div className={styles.formContainer}>
            <div className={styles.toolbarContainer}>
              <Title2 className={styles.fullWidth}>{filterCount > 0 ? `Filters (${filterCount})` : "Filters"}</Title2>
            </div>
            <div className={mergeClasses(styles.formRow, styles.filterForm)}>
              <div className={mergeClasses(styles.filterInputsContainer)}>
                <FilterTextFields
                  title="Title"
                  value={title}
                  placeholder="Insert document title as filter"
                  onChange={onTitleTextChange}
                />
                <FilterTextFields
                  title="Keyword"
                  value={keywordText}
                  placeholder="Insert a keyword or phrase as filter"
                  onChange={onKeywordTextChange}
                />
                {/* {isFetching && <Loading size="large" />} */}
                <GroupedMetadataSelector
                  selectedOptions={selectedMetadataOptions}
                  onChange={(selectedOptions) => setSelectedMetadataOptions(selectedOptions)}
                  appId={appId}
                />
              </div>
            </div>
            <ActionButtons
              onUpdate={onUpdateSuggestion}
              onReset={clearFilters}
              className={mergeClasses(styles.formRow, styles.formActionContainer)}
              updateLabel="Update Results"
              resetLabel="Clear All Filters"
              haveWidth={true}
              btnClassName={styles.formActionBtn}
            />
          </div>
        </PopoverSurface>
      </Popover>
    );
  },
  (_prev, _next) => false
);
